/*
 *  -----------------------------------------------------------------------
 *    <copyright file="step2-data.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2022 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { Dispatch, forwardRef, SetStateAction, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'hooks';
import {
    ReportDefinitionFields,
    ReportDefinitionFields_ReportDefinition_tabs_ReportDefinitionTab as ReportDefinitionFields_tabs,
} from 'queries/generated/fragments.generated';
import { ReportDefinitionTabs } from 'types/graphql-global-types';
import MultiListDragDrop, { DragDropListItem } from 'views/components/core/multi-list-drag-drop';
import {
    clearColumnsByTabType,
    containsTab,
    getNotMatchingTabs,
    getTab,
} from 'views/components/reports/custom-report-config/custom-report-helpers';
import { AddEditCustomReportErrors, AddEditCustomReportState, CustomReportPage } from './index';

interface CustomReportStep2DataProps {
    show: boolean;
    optionsTabs: ReportDefinitionFields_tabs[];
    reportDefinition: ReportDefinitionFields;
    state: AddEditCustomReportState;
    setState: Dispatch<SetStateAction<AddEditCustomReportState>>;
    getDefaultOptionsTab: (tabType: ReportDefinitionTabs) => ReportDefinitionFields_tabs;
    reset: () => void;
    updateErrorState: (errorState: AddEditCustomReportErrors, pageNumber: number) => void;
}

const CustomReportStep2Data = forwardRef(
    (
        {
            show,
            optionsTabs,
            reportDefinition,
            state,
            setState,
            getDefaultOptionsTab,
            reset,
            updateErrorState,
        }: CustomReportStep2DataProps,
        ref,
    ) => {
        const { t } = useTranslation();

        const initialErrors: AddEditCustomReportErrors = {
            tabs: '',
        };

        const reportDefinitionTabs = state.reportDefinition.tabs ?? [];
        const reportDefinitionTabsLength = reportDefinitionTabs.length ?? 0;

        const [errors, setErrors] = useState({ ...initialErrors } as AddEditCustomReportErrors);
        const [notSelectedTabs, setNotSelectedTabs] = useState([] as ReportDefinitionFields_tabs[]);

        const getNotSelectedTabs = () =>
            optionsTabs.filter(
                (optionsTab: ReportDefinitionFields_tabs) => !containsTab(reportDefinitionTabs, optionsTab.tabType),
            );

        function validationOnUpdate() {
            if (reportDefinitionTabsLength > 0) {
                setErrors({ tabs: '' });
                return true;
            }

            setErrors({ tabs: t('reports:customReportNoTabsSelectedError') });
            return false;
        }

        useImperativeHandle(ref, () => ({
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        const getListItems = (inputTabs: (ReportDefinitionFields_tabs | null)[] | null) =>
            inputTabs?.map(
                (tab, index) =>
                    ({
                        key: tab?.tabType,
                        text: t(`reports:${tab?.tabType}`),
                        index,
                    }) as DragDropListItem,
            ) ?? [];

        const getUnchangedReportDefinitionTabs = (listItems: DragDropListItem[]): ReportDefinitionFields_tabs[] => {
            const unchangedListItems = listItems.filter((item: DragDropListItem) =>
                containsTab(reportDefinition.tabs, item.key as ReportDefinitionTabs),
            );

            return unchangedListItems.map((listItem) => {
                const tabType = listItem.key as ReportDefinitionTabs;
                const tab = getTab(reportDefinition.tabs, tabType);

                return {
                    __typename: 'ReportDefinitionTab',
                    tabType,
                    columns: [...(tab?.columns ?? [])],
                } as ReportDefinitionFields_tabs;
            });
        };

        const addNewTab = (
            tabType: ReportDefinitionTabs,
            updatedTabs: ReportDefinitionFields_tabs[],
            index: number,
            updatedOptionsTabs: ReportDefinitionFields_tabs[],
        ) => {
            const newTab = getDefaultOptionsTab(tabType);
            updatedTabs.splice(index, 0, newTab);
            clearColumnsByTabType(updatedOptionsTabs, tabType);
        };

        const updateTabs = (
            list2Items: DragDropListItem[],
            updatedTabs: ReportDefinitionFields_tabs[],
            updatedOptionsTabs: ReportDefinitionFields_tabs[],
        ) => {
            list2Items.forEach((item: DragDropListItem, index: number) => {
                const tabType = item.key as ReportDefinitionTabs;

                if (!containsTab(updatedTabs, tabType)) {
                    addNewTab(tabType, updatedTabs, index, updatedOptionsTabs);
                }
            });
        };

        const getUnchangedOptionsTabs = (list1Items: DragDropListItem[]) => {
            const tabs = list1Items.map((item: DragDropListItem) =>
                getDefaultOptionsTab(item.key as ReportDefinitionTabs),
            );

            const selectedOptionsTabs = getNotMatchingTabs(optionsTabs, tabs);

            return [...tabs, ...selectedOptionsTabs];
        };

        const updateState = (list1Items: DragDropListItem[], list2Items: DragDropListItem[]) => {
            const updatedTabs = getUnchangedReportDefinitionTabs(list2Items);
            const updatedOptionsTabs = getUnchangedOptionsTabs(list1Items);

            updateTabs(list2Items, updatedTabs, updatedOptionsTabs);

            setState({
                ...state,
                reportDefinition: {
                    ...state.reportDefinition,
                    tabs: updatedTabs,
                },
                optionsTabs: updatedOptionsTabs,
            });
        };

        const update = (list1Items: DragDropListItem[], list2Items: DragDropListItem[]) => {
            updateState(list1Items, list2Items);
        };

        useEffect(() => {
            updateErrorState(errors, CustomReportPage.step2Data);
        }, [errors]);

        useEffect(() => {
            setNotSelectedTabs(getNotSelectedTabs());
        }, [optionsTabs]);

        useEffect(() => {
            if (reportDefinitionTabsLength > 0) setErrors({ tabs: t('') });
        }, [reportDefinitionTabs]);

        return (
            <>
                {show && (
                    <div className="step2-data">
                        <MultiListDragDrop
                            updateParent={update}
                            list1Items={getListItems(notSelectedTabs)}
                            list2Items={getListItems(reportDefinition.tabs)}
                            list1Header={t('reports:customReportStep3ColumnsNotSelected')}
                            list2Header={t('reports:customReportStep3ColumnsSelected')}
                            reset={reset}
                            error={errors.tabs}
                        />
                    </div>
                )}
            </>
        );
    },
);

export default CustomReportStep2Data;
